import React, { useState, FC, useEffect } from 'react';
import { buildLocStoreKeys } from 'store/types';

interface IContextState {
  setPermissions: (permissions: string[]) => void;
  checkPermission: (permissions: string) => boolean;
  permissions: string[] | null;
}

export const PermissionsContext = React.createContext<IContextState>({
  setPermissions: () => {},
  checkPermission: () => false,
  permissions: null,
});

interface IProps {
  children: React.ReactNode;
}

export const PermissionsProvider: FC<IProps> = ({ children }: IProps) => {
  const LocStoreKeys = buildLocStoreKeys();

  const [permissions, setPermissions] = useState<string[] | null>(
    window.localStorage.getItem(LocStoreKeys.PERMISSIONS)?.split(',') || null
  );

  /**
   * Subscribe to local storage updates to handle the case
   * if app is open in more than one tab
   * and permissions has been updated in any of the tabs
   */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === LocStoreKeys.PERMISSIONS) {
        setPermissions(e.newValue?.split(',') || null);
      }
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  });

  const setPermissionsStateAndStore = (prms: string[]) => {
    setPermissions(prms);
    if (prms?.length) return window.localStorage.setItem(LocStoreKeys.PERMISSIONS, prms.join(','));

    return window.localStorage.removeItem(LocStoreKeys.PERMISSIONS);
  };

  const checkPermission = (permission: string) => {
    return permissions?.includes(permission) || false;
  };

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        setPermissions: setPermissionsStateAndStore,
        checkPermission,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export function usePermissionsContext(): IContextState {
  return React.useContext(PermissionsContext);
}
